import $ from "jquery";
import toastr from "toastr";
import Vue from "vue";

import { audioService } from "./audio-service";
import ToastrType from "./toastr-types.interface";
import UrlParamsRetreiver from "./url-params-retreiver";

// Init PlayerService for global use.
export const playerService = new Vue({
  data() {
    return {
      trackCache: [],
    };
  },

  methods: {
    /**
     * Gets a specific track if possible from the track cache.
     *
     * @param tracklistId
     * @param trackId
     * @return {*}
     */
    getTrackFromCache: function (tracklistId, trackId) {
      // Check if this track-list id exists in the track cache. If not, create it.
      if (typeof this.trackCache[tracklistId] === "undefined") {
        this.setTrackCache(tracklistId, {});
      }

      if (
        typeof this.trackCache[tracklistId][trackId] == "undefined" ||
        this.trackCache[tracklistId][trackId] == false
      ) {
        return false;
      }

      return this.trackCache[tracklistId][trackId];
    },

    /**
     * Gets a specific track if possible from the track cache.
     *
     * @param tracklistId
     * @return {*}
     */
    getTrackListCache: function (tracklistId) {
      // Doesn't exist yet. create it with initial collection.
      if (typeof this.trackCache[tracklistId] === "undefined") {
        this.setTrackCache(tracklistId, {});
      }

      return this.trackCache[tracklistId];
    },

    /**
     * Sets the track collection for this track-list in the track cache.
     *
     * @param tracklistId
     * @param tracks
     * @return {boolean}
     */
    setTrackCache: function (tracklistId, tracks) {
      this.trackCache[tracklistId] = tracks;

      return true;
    },

    /**
     * Adds to the track collection for this track-list in the track cache.
     *
     * @param tracklistId
     * @param key
     * @param value
     * @return {boolean}
     */
    addToTrackCache: function (tracklistId, key, value) {
      // Doesn't exist yet. create it with initial collection.
      if (typeof this.trackCache[tracklistId] === "undefined") {
        this.setTrackCache(tracklistId, {});
      }

      this.trackCache[tracklistId][key] = value;

      return true;
    },

    /**
     * Display a Toast notification we predefined Toastr types.
     * > SUGAR SYNTAX
     *
     * @param message
     * @param type
     */
    toast: function (message, type = ToastrType.Info, options = {}) {
      toastr[type](message, null, options);
    },

    /**
     * Throws event that the player-app catches, passes it down to
     * audio-player and changes the now playing info.
     *
     * @param trackId
     * @param silent
     * @returns {boolean}
     */
    streamSharedTrack: function (trackId, silent = false) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof trackId === "undefined") {
        if (window.adminProjectShare == true && silent == false) {
          this.toast(
            "[PLYR_060851] Error: Could not stream shared track. Invalid track id given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      if (silent == false) {
        // Show the user that something is in progress.
        playerService.$emit(
          "loader-text-show",
          "Configuring Player for shared Track",
        );
      }

      // Remove it from the database.
      $.ajax({
        method: "GET",
        url: `/api/tracks/${trackId}`,
        cache: false,
      })
        .done(function (source) {
          let track = source.data;
          track.noAutoplay = true;

          // Create a timestamp
          audioService.stream(track);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true && silent == false) {
            playerService.toast(
              "[PLYR_60855] Error: Could not stream shared track.",
              ToastrType.Error,
            );
          }
        })
        .always(function (responseData) {
          // Hide our player loader.
          playerService.$emit("loader-hide");
        });
    },

    /**
     * Ability to download a track.
     *
     * @param track
     * @returns {boolean}
     */
    download: function (track) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof track === "undefined" || typeof track.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_241425] Error: Could not download track. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("download-track", track);
    },

    /**
     * Ability to download a track as guest.
     *
     * @param track
     * @returns {boolean}
     */
    downloadasGuest: function (track) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof track === "undefined" || typeof track.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_241425] Error: Could not download track. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("download-track-as-guest", track);
    },

    /**
     * Ability to download a track.
     *
     * @param trackId
     */
    downloadTrackById: function (trackId) {
      let ctx = this;

      // Remove it from the database.
      $.ajax({
        method: "GET",
        url: `/api/tracks/${trackId}`,
        cache: false,
      })
        .done(function (source) {
          let track = source.data;

          ctx.download(track);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true && silent == false) {
            playerService.toast("Cannot download track.", ToastrType.Error);
          }
        });
    },

    /**
     * Load any projects via the API.
     */
    loadProjects: function () {
      let ctx = this;

      $.ajax({
        method: "GET",
        url: `/api/projects`,
        cache: false,
      })
        .done(function (source) {
          let projects = [];

          for (let projectIndex in source.data.data) {
            if (source.data.data.hasOwnProperty(projectIndex)) {
              let project = source.data.data[projectIndex];

              projects.push(project);

              if (project.notify === 1) {
                $.ajax({
                  method: "POST",
                  url: "/api/projects/" + project.id + "/notified",
                  cache: false,
                }).done(function (response) {
                  playerService.toast(
                    "Project has been added (" + project.name + ")",
                    ToastrType.Info,
                  );
                });
              }
            }
          }

          playerService.$emit("projects-updated", projects);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_301337] Error: Could not create new Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not create new Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Load a project via ID. via the API.
     */
    openProject: function (projectId, fromShareUrl = false) {
      if (typeof projectId === "undefined" || projectId === false) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_21122] Error: Could not load Project. Invalid project id given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      $.ajax({
        method: "GET",
        url: `/api/projects/${projectId}`,
        cache: false,
      })
        .done(function (source) {
          let project = source.data;

          if (window.adminProjectShare == true) {
            ctx.toast(
              `Viewing ${fromShareUrl ? "shared " : ""}project: ${
                project.name
              }`,
              ToastrType.Info,
            );
          }

          playerService.$emit("view-project-loaded", project);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              `[PLYR_21123] Error: Could not load ${
                fromShareUrl ? "shared " : ""
              }Project.`,
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Opens the modal to fill in a few details before you store the new project.
     */
    storeProject: function (name, description, color) {
      let ctx = this;

      $.ajax({
        method: "POST",
        url: `/api/projects`,
        data: {
          name: name,
          description: description,
          color: color,
        },
        cache: false,
      })
        .done(function (source) {
          let project = source.data;

          playerService.toast(`New project created`, ToastrType.Success);

          window.webpackCloseBsModalWorkaround("#createProjectModal");

          playerService.$emit("project-added", project);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_301337] Error: Could not create new Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not create new Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Adds a new project on the server and in the app.
     */
    addProject: function (convertingQuicklist = false) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      playerService.$emit("create-project", convertingQuicklist);
    },

    /**
     * Delete a specific project.
     *
     * @param project
     */
    deleteProject: function (project) {
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          playerService.toast(
            "[PLYR_61048] Error: Could not delete project.",
            ToastrType.Error,
          );
        } else {
          playerService.toast(
            "Error: Could not delete project.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Remove it from the database.
      $.ajax({
        method: "POST",
        url: `/api/projects/` + project.id,
        data: {
          _method: "delete",
        },
        cache: false,
      })
        .done(function (source) {
          let projects = [];

          for (let projectIndex in source.data.data) {
            if (source.data.data.hasOwnProperty(projectIndex)) {
              let project = source.data.data[projectIndex];

              projects.push(project);
            }
          }

          playerService.$emit("projects-updated", projects);

          playerService.$emit("project-deleted", project.id);

          window.webpackOpenBsTabWorkaround("#search-track-results-tab");

          playerService.$emit("default-tab-color");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_61101] Error: Could not delete Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not delete Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Ability to edit a project.
     *
     * @param project
     * @returns {boolean}
     */
    editProject: function (project) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61521] Error: Could not edit Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("edit-project", project);
    },

    /**
     * Ability to share/email a project.
     *
     * @param project
     * @returns {boolean}
     */
    shareProject: function (project) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_20900] Error: Could not share Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("share-project", project);
    },

    assigntoUser: function (project) {
      if (typeof project === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_20900] Error: Could not assign to User. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("assign-to-user", project);
    },

    /**
     * Ability to share/email a album.
     *
     * @param album
     * @returns {boolean}
     */
    shareAlbum: function (album) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof album === "undefined" || typeof album.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_20900] Error: Could not share Album. Invalid album given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("share-album", album);
    },

    /**
     * Ability to share/email an artist.
     *
     * @param album
     * @returns {boolean}
     */
    shareArtist: function (artist) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof artist === "undefined" || typeof artist.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_20900] Error: Could not share Artist. Invalid album given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("share-artist", artist);
    },

    /**
     * Ability to share/email a library.
     *
     * @returns {boolean}
     * @param libraryId
     */
    shareLibrary: function (libraryId) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof libraryId === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_20900] Error: Could not share Library. Invalid library given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("share-library", libraryId);
    },

    /**
     * Ability to share/email a playlist.
     *
     * @param playlist
     * @returns {boolean}
     */
    sharePlaylist: function (playlist) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (
        typeof playlist === "undefined" ||
        typeof playlist.id === "undefined"
      ) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_171444] Error: Could not share Playlist. Invalid playlist given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("share-playlist", playlist);
    },

    /**
     * Opens the modal for downloading collection tracks, allows to select file types + custom resource.
     *
     * @returns {boolean}
     * @param resourceName
     * @param resourceId
     * @param resourceType
     * @param resourceTrackcount
     */
    openDownloadTrackCollection: function (
      resourceName,
      resourceId,
      resourceType,
      resourceTrackcount,
    ) {
      if (resourceType !== "shared-project") {
        if (this.featureRequiresAuthenticationCheck() == false) {
          return false;
        }
      }

      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (
        typeof resourceName === "undefined" ||
        typeof resourceId === "undefined" ||
        typeof resourceType === "undefined" ||
        typeof resourceTrackcount === "undefined"
      ) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_191554] Error: Could not open download collection tracks modal. Invalid collection given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      this.$emit(
        "open-download-track-collection",
        resourceName,
        resourceId,
        resourceType,
        resourceTrackcount,
      );
    },

    /**
     * Download track collection.
     *
     * @param resourceType
     * @param resourceId
     * @param filetype
     * @returns {boolean}
     */
    downloadTrackCollection: function (
      resourceType,
      resourceId,
      filetype,
      trackCount,
    ) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (
        typeof resourceType == "undefined" ||
        typeof resourceId === "undefined"
      ) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_11353] Error: Could not download collection tracks. Invalid collection given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      // Announce the success of this action.
      playerService.toast(`Download Initialising.`, ToastrType.info);

      (function ($) {
        $.fn.bmdIframe = function (options) {
          var self = this;
          var settings = $.extend(
            {
              defaultW: 640,
              defaultH: 660,
            },
            options,
          );
          var dataContent = {
            src: `${location.protocol}//${window.location.host}/api/download-resource/${resourceType}/${resourceId}/${filetype}`,
            height: settings.defaultH,
            width: settings.defaultW,
          };
          $(self).find("iframe").attr(dataContent);
          this.on("hidden.bs.modal", function () {
            $(this).find("iframe").html("").attr("src", "");
            $("#myModal").modal("hide");
          });
        };
        $("#downloadModal").bmdIframe();
        $("#downloadModal").modal("show");
        $("#downloadModal").show("slide", { direction: "down" }, 800);

        window.webpackCloseBsModalWorkaround("#downloadTrackModal");

        // dismiss modal after getting message from iframe
        window.addEventListener("message", (e) => {
          if (e.data === "dismissModal") {
            $("#downloadModal").hide("slide", { direction: "down" }, 800);
          }
        });
      })(jQuery);
    },

    downloadTrack: function (trackId, filetype) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof trackId === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_11353] Error: Could not download collection tracks. Invalid collection given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      // Announce the success of this action.
      playerService.toast(`Download Initialising.`, ToastrType.info);

      (function ($) {
        $.fn.bmdIframe = function (options) {
          var self = this;
          var settings = $.extend(
            {
              defaultW: 640,
              defaultH: 660,
            },
            options,
          );
          var dataContent = {
            src: `${location.protocol}//${window.location.host}/api/tracks/${trackId}/download/${filetype}`,
            height: settings.defaultH,
            width: settings.defaultW,
          };
          $(self).find("iframe").attr(dataContent);
          this.on("hidden.bs.modal", function () {
            $(this).find("iframe").html("").attr("src", "");
            $("#myModal").modal("hide");
          });
        };
        $("#downloadModal").bmdIframe();

        window.webpackCloseBsModalWorkaround("#downloadTrackModal");
      })(jQuery);
    },

    downloadTrackasGuest: function (trackId, filetype) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof trackId === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_11353] Error: Could not download collection tracks. Invalid collection given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      // Announce the success of this action.
      playerService.toast(`Download Initialising.`, ToastrType.info);

      (function ($) {
        $.fn.bmdIframe = function (options) {
          var self = this;
          var settings = $.extend(
            {
              defaultW: 640,
              defaultH: 660,
            },
            options,
          );
          var dataContent = {
            src: `${location.protocol}//${window.location.host}/api/tracks/${trackId}/download/${filetype}?guest=true`,
            height: settings.defaultH,
            width: settings.defaultW,
          };
          $(self).find("iframe").attr(dataContent);
          this.on("hidden.bs.modal", function () {
            $(this).find("iframe").html("").attr("src", "");
            $("#myModal").modal("hide");
          });
        };
        $("#downloadModal").bmdIframe();

        window.webpackCloseBsModalWorkaround("#downloadTrackModal");
      })(jQuery);
    },

    /**
     * Download album tracks.
     *
     * @param album
     * @returns {boolean}
     */
    downloadAlbumTracks: function (album) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof album === "undefined" || typeof album.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_11353] Error: Could not download album tracks. Invalid album given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      // Announce the success of this action.
      playerService.toast(`Download Initialising.`, ToastrType.info);

      // jquery make a post request to the download endpoint.
      $.post(`/api/albums/${album.id}/download-tracks`, function (data) {
        console.log("download data", data);
        if (data.success) {
          window.open(data.data, "_blank");
        } else {
          this.toast(
            "[PLYR_99001] Error: Unable to download tracks.",
            ToastrType.Error,
          );
        }
      });

      // Open a new window with the right download response headers.
      // window.open(`/api/albums/${album.id}/download-tracks`, "_blank");
    },

    /**
     * Ability to email the project cue sheet to a person.
     *
     * @param project
     * @returns {boolean}
     */
    shareCueSheet: function (project) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_51022] Error: Could not share Project cue sheet. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("email-cue-sheet", project);
    },

    /**
     * Ability to email the project cue sheet to a person.
     *
     * @param project
     * @returns {boolean}
     */
    openDownloadCueSheet: function (project) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_51022] Error: Could not share Project cue sheet. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("download-cue-sheet", project);
    },

    /**
     * Ability to share/email a track.
     *
     * @param track
     * @returns {boolean}
     */
    shareTrack: function (track) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof track === "undefined" || typeof track.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_50947] Error: Could not share Track. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("share-track", track);
    },

    /**
     * Ability to find similar tracks using AIMS
     *
     * @param track
     * @returns {boolean}
     */
    findSimilar: function (track) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof track === "undefined" || typeof track.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_50947] Error: Could not share Track. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      playerService.$emit("find-similar-clicked", track.id, track.title);
    },

    instantsaveManageProjects: function (sortedProjectIds, options = {}) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof sortedProjectIds === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_121239] Error: Could not save Managed Projects.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Remove it from the database.
      // JSON, not form fields: two fields per project exceeds PHP's max_input_vars (1000) past 500 projects.
      $.ajax({
        method: "POST",
        url: `/api/projects/manage`,
        contentType: "application/json",
        data: JSON.stringify({
          sorted: sortedProjectIds,
          refresh: options.refreshProjects !== false,
        }),
        cache: false,
      })
        .done(function (source) {
          if (options.showSuccessToast !== false) {
            // Announce the success of this action.
            playerService.toast(
              `Projects have been updated.`,
              ToastrType.Success,
            );
          }

          // Guard against unexpected response payloads (e.g. an error body
          // without the nested data object) so a bad response doesn't throw
          // or wipe the project tabs with an empty collection.
          if (source && source.data && source.data.data) {
            let projects = [];

            for (let projectIndex in source.data.data) {
              if (source.data.data.hasOwnProperty(projectIndex)) {
                let project = source.data.data[projectIndex];

                projects.push(project);
              }
            }

            playerService.$emit("projects-updated", projects);
          }

          playerService.$emit("manage-projects-updated", ctx.projects);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_121240] Error: Could not save Managed Projects.",
              ToastrType.Error,
            );
          }
        });
    },

    saveProjectTrackOrdering: function (projectId, sortedTrackIds) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (
        typeof sortedTrackIds === "undefined" ||
        typeof projectId === "undefined"
      ) {
        this.toast(
          "[PLYR_181200] Error: Could not save track ordering.",
          ToastrType.Error,
        );

        return false;
      }

      let ctx = this;

      // Remove it from the database.
      $.ajax({
        method: "POST",
        url: `/api/projects/${projectId}/updatetrackordering`,
        data: {
          projectId: projectId,
          sorted: sortedTrackIds,
        },
        cache: false,
      })
        .done(function (project) {
          // Announce the success of this action.
          playerService.toast(
            `Track Ordering has been updated.`,
            ToastrType.Success,
          );

          playerService.$emit("project-updated-" + project.id, project);
        })
        .fail(function (responseData) {
          playerService.toast(
            "[PLYR_121240] Error: Could not save track ordering.",
            ToastrType.Error,
          );
        });
    },

    saveManageProjects: function (sortedProjectIds) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof sortedProjectIds === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_121239] Error: Could not save Managed Projects.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Remove it from the database.
      $.ajax({
        method: "POST",
        url: `/api/projects/manage`,
        contentType: "application/json",
        data: JSON.stringify({ sorted: sortedProjectIds }),
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            `Projects have been updated.`,
            ToastrType.Success,
          );

          let projects = [];

          for (let projectIndex in source.data.data) {
            if (source.data.data.hasOwnProperty(projectIndex)) {
              let project = source.data.data[projectIndex];

              projects.push(project);
            }
          }

          playerService.$emit("projects-updated", projects);

          playerService.$emit("manage-projects-updated", ctx.projects);

          // Auto close the modal.
          window.webpackCloseBsModalWorkaround("#manageProjectsModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_121240] Error: Could not save Managed Projects.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Save contents of an edited project.
     *
     * @param project
     * @returns {boolean}
     */
    saveProject: function (project) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not save Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Remove it from the database.
      $.ajax({
        method: "POST",
        url: `/api/projects/` + project.id,
        data: {
          _method: "put",
          name: project.name,
          description: project.description,
          color: project.color ? project.color : null,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            `Project updated: ${project.name}.`,
            ToastrType.Success,
          );

          playerService.$emit("project-saved", project);

          playerService.$emit("project-updated-" + project.id, project);

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#editProjectModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_61540] Error: Could not save Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not save Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Saves the tracks from a project that was shared with you to your own new project.
     *
     * @param id projectId or playlistId
     * @param type "project" or "playlist"
     * @param options
     * @returns {boolean}
     */
    saveSharedProjectAsNewProject(id, type, options = {}) {
      if (this.featureRequiresAuthenticationCheck() === false) {
        return false;
      }

      const ctx = this;
      const hideSaveProjectButton =
        typeof options.hideSaveProjectButton === "undefined"
          ? true
          : options.hideSaveProjectButton;
      const openCreatedProject =
        typeof options.openCreatedProject === "undefined"
          ? true
          : options.openCreatedProject;
      const successMessage =
        typeof options.successMessage !== "undefined"
          ? options.successMessage
          : `Shared ${
              type === "project" ? "Project" : "Playlist"
            } Tracks saved as New Project.`;

      $.ajax({
        url: `/api/projects/save-shared-project`,
        method: "POST",
        data: {
          id: id,
          type: type,
        },
        cache: false,
      })
        .done((source) => {
          // Announce the success of this action.
          playerService.toast(successMessage, ToastrType.Success);

          if (hideSaveProjectButton) {
            playerService.$emit("hide-save-project-button");
          }

          let projectToEdit = false;
          let projects = [];
          let newestProjectId = 0;

          for (let projectIndex in source.data.data) {
            if (source.data.data.hasOwnProperty(projectIndex)) {
              let project = source.data.data[projectIndex];

              projects.push(project);

              if (Number(project.id) > newestProjectId) {
                newestProjectId = Number(project.id);
                projectToEdit = project;
              }
            }
          }

          if (projectToEdit !== false) {
            playerService.$emit(
              "projects-updated",
              projects,
            );

            setTimeout(() => {
              playerService.$emit("project-added", projectToEdit, projects);
            }, 250);

            if (openCreatedProject) {
              ctx.editProject(projectToEdit);
            }
          } else {
            playerService.$emit("projects-updated", projects);
          }
        })
        .fail((responseData) => {
          if (type === "playlist") {
            const responseJson = responseData.responseJSON
              ? responseData.responseJSON
              : {};
            const responseMessage =
              typeof responseJson.message !== "undefined"
                ? responseJson.message
                : null;

            if (responseData.status === 422) {
              playerService.toast(
                responseMessage
                  ? responseMessage
                  : "This Playlist has no tracks to save.",
                ToastrType.Error,
              );
              return;
            }

            if (responseData.status === 401) {
              playerService.$emit("feature-requires-auth-modal");
              return;
            }

            if (responseData.status === 419) {
              playerService.$emit("open-browser-idle-too-long-modal");
              return;
            }

            playerService.toast(
              "Something went wrong saving this Playlist. Please try again.",
              ToastrType.Error,
            );
            return;
          }

          if (window.adminProjectShare === true) {
            playerService.toast(
              "[PLYR_141522] Error: Could not save Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not save Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Saves a playlist as a new project from the playlists tab card action.
     *
     * @param playlistId
     * @returns {boolean}
     */
    savePlaylistAsProject(playlistId) {
      return this.saveSharedProjectAsNewProject(playlistId, "playlist", {
        successMessage: "Playlist saved as a new Project.",
        hideSaveProjectButton: false,
        openCreatedProject: false,
      });
    },

    /**
     * Saves the track that was shared with you to your own new project.
     *
     * @returns {boolean}
     * @param trackId
     */
    saveTrackAsNewProject: function (trackId) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      let ctx = this;

      $.ajax({
        url: `/api/projects/save-track-as-new-project`,
        method: "POST",
        data: {
          track: trackId,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            `Shared Track saved as New Project.`,
            ToastrType.Success,
          );

          let projects = [];

          for (let projectIndex in source.data.data) {
            if (source.data.data.hasOwnProperty(projectIndex)) {
              let project = source.data.data[projectIndex];

              projects.push(project);
            }
          }

          playerService.$emit("projects-updated", projects);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_261217] Error: Could not save Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not save Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Save the Quicklist as a Project and empty its contents.
     *
     * @returns {boolean}
     * @param name
     * @param description
     * @param color
     */
    saveQuicklistAsProject: function (name, description, color) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      let ctx = this;

      $.ajax({
        url: `/api/projects/convert-quicklist`,
        method: "POST",
        data: {
          name: name,
          description: description,
          color: color,
        },
        cache: false,
      })
        .done(function (source) {
          window.webpackCloseBsModalWorkaround("#createProjectModal");

          // Announce the success of this action.
          playerService.toast(
            `Quick List copied to a new Project.`,
            ToastrType.Success,
          );

          let projects = [];

          for (let projectIndex in source.data.data) {
            if (source.data.data.hasOwnProperty(projectIndex)) {
              let project = source.data.data[projectIndex];

              projects.push(project);
            }
          }

          playerService.$emit("projects-updated", projects);

          playerService.$emit("quicklist-converted-successfully");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_61540] Error: Could not save Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "[PLYR_61540] Error: Could not save Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Removes all the tracks from the users quicklist.
     */
    removeQuicklistTracks: function () {
      if (window.authenticatedUserEmail == false) {
        // Clear the LS quicklist.
        playerService.setLs("quicklistguest", JSON.stringify([]));

        window.quicklistTrackIds = [];

        playerService.$emit("quicklist-guest-ls-updated");
        playerService.$emit("quicklist-updated");

        // Announce the success of this action.
        playerService.toast(
          `All Quicklist Tracks have been removed.`,
          ToastrType.Success,
          {
            timeOut: 1250,
            extendedTimeOut: 1000,
          },
        );

        return true;
      } else {
        let ctx = this;

        $.ajax({
          url: `/api/projects/remove-all-quicklist-tracks`,
          method: "GET",
          cache: false,
        })
          .done(function (source) {
            // Announce the success of this action.
            playerService.toast(
              `All Quicklist Tracks have been removed.`,
              ToastrType.Success,
              {
                timeOut: 1250,
                extendedTimeOut: 1000,
              },
            );

            let projects = [];

            for (let projectIndex in source.data.data) {
              if (source.data.data.hasOwnProperty(projectIndex)) {
                let project = source.data.data[projectIndex];

                projects.push(project);
              }
            }

            playerService.$emit("projects-updated", projects);
            playerService.$emit("quicklist-updated", projects);
          })
          .fail(function (responseData) {
            if (window.adminProjectShare == true) {
              playerService.toast(
                "[PLYR_11530] Error: Could not clear Quicklist Tracks..",
                ToastrType.Error,
              );
            }
          });
      }
    },

    /**
     * Emails a Project to a person with optional notes.
     *
     * @param project
     * @param toEmail
     * @param fromEmail
     * @param notes
     * @param title
     * @param guestDownload
     * @returns {boolean}
     */
    emailProject: function (
      project,
      toEmail,
      fromEmail,
      notes,
      title,
      guestDownload = false,
    ) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_20916] Error: Could not email Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      if ("quicklistGuest" == project) {
        project = { id: 0 };
      }

      // Send this email/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/projects/${project.id}/share-email`,
        data: {
          project: project.id,
          toEmail: toEmail,
          fromEmail: fromEmail,
          notes: notes,
          title: title,
          hash: window.sharehash,
          guest_download: guestDownload,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            "Project emailed successfully.",
            ToastrType.Success,
          );

          playerService.$emit("project-shared", project, toEmail, notes);

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#shareProjectModal");
        })
        .fail(function (responseData) {
          playerService.$emit("sharing-failed");

          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_20921] Error: Could not share Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not share Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Emails a Album to a person with optional notes.
     *
     * @param album
     * @param email
     * @param notes
     * @param fromEmail
     * @returns {boolean}
     */
    emailAlbum: function (album, email, notes, fromEmail) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof album === "undefined" || typeof album.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_21104] Error: Could not email Album. Invalid album given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this email/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/albums/${album.id}/share-email`,
        data: {
          album: album["id"],
          email: email,
          notes: notes,
          fromEmail: fromEmail,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            "Album emailed successfully.",
            ToastrType.Success,
          );

          playerService.$emit("album-shared", album, email, notes);

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#shareAlbumModal");
        })
        .fail(function (responseData) {
          playerService.toast(
            "[PLYR_21105] Error: Could not share Album.",
            ToastrType.Error,
          );
        });
    },

    /**
     * Emails a Artist to a person with optional notes.
     *
     * @param artist
     * @param email
     * @param notes
     * @param fromEmail
     * @returns {boolean}
     */
    emailArtist: function (artist, email, notes, fromEmail) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof artist === "undefined" || typeof artist.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_21104] Error: Could not email Artist. Invalid artist given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this email/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/artists/${artist.id}/share-email`,
        data: {
          artist: artist,
          email: email,
          notes: notes,
          fromEmail: fromEmail,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            "Artist emailed successfully.",
            ToastrType.Success,
          );

          playerService.$emit("artist-shared", artist, email, notes);

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#shareArtistModal");
        })
        .fail(function (responseData) {
          playerService.toast(
            "[PLYR_21105] Error: Could not share Artist.",
            ToastrType.Error,
          );
        });
    },

    /**
     * Emails a Library to a person with optional notes.
     *
     * @param libraryId
     * @param email
     * @param notes
     * @param fromEmail
     * @returns {boolean}
     */
    emailLibrary: function (libraryId, email, notes, fromEmail) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof libraryId === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_21104] Error: Could not email Library. Invalid library given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this email/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/libraries/${libraryId}/share-email`,
        data: {
          libraryId: libraryId,
          email: email,
          notes: notes,
          fromEmail: fromEmail,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            "Library emailed successfully.",
            ToastrType.Success,
          );

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#shareLibraryModal");
        })
        .fail(function (responseData) {
          playerService.toast(
            "[PLYR_28112] Error: Could not share Library.",
            ToastrType.Error,
          );
        });
    },

    /**
     * Emails a Playlist to a person with optional notes.
     *
     * @param playlist
     * @param email
     * @param notes
     * @param fromEmail
     * @returns {boolean}
     */
    emailPlaylist: function (playlist, email, notes, fromEmail) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (
        typeof playlist === "undefined" ||
        typeof playlist.id === "undefined"
      ) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_21104] Error: Could not email Playlist. Invalid playlist given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this email/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/playlists/${playlist.id}/share-email`,
        data: {
          playlist: playlist,
          email: email,
          notes: notes,
          fromEmail: fromEmail,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            "Playlist emailed successfully.",
            ToastrType.Success,
          );

          playerService.$emit("playlist-shared", playlist, email, notes);

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#sharePlaylistModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_21105] Error: Could not share Playlist.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "[PLYR_21105] Error: Could not share Playlist.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Requests a license for a specified track.
     *
     * @param project
     * @param email
     * @param notes
     * @returns {boolean}
     */
    requestTrackLicense: function (track, name, email, notes) {
      // Check all our fields are here.
      if (typeof track === "undefined" || typeof track.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_101108] Error: Could not request license for Track.. Invalid track given.",
            ToastrType.Error,
          );
        } else {
          this.toast(
            "Error: Could not request license for Track.. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      if (typeof name === "undefined" || typeof email === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_101108] Error: Could not request license for Track.. Invalid user details given.",
            ToastrType.Error,
          );
        } else {
          this.toast(
            "Error: Could not request license for Track.. Invalid user details given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this email/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/licenses`,
        data: {
          track: track,
          name: name,
          email: email,
          notes: notes,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            `License Request successfully submitted.`,
            ToastrType.Success,
          );

          playerService.$emit(
            "track-license-requested",
            track,
            name,
            email,
            notes,
          );

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#licenseTrackModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_101107] Error: Could not request license for track.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not request license for track.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Emails a Track to a person with optional notes.
     *
     * @param track
     * @param email
     * @param notes
     * @param fromname
     * @returns {boolean}
     */
    emailTrack: function (track, email, notes, fromname) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof track === "undefined" || typeof track.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_50956] Error: Could not email Track. Invalid track given.",
            ToastrType.Error,
          );
        } else {
          this.toast(
            "Error: Could not email Track. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this email/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/tracks/${track.id}/share-email`,
        data: {
          track: {
            id: track.id,
            title: track.title,
          },
          email: email,
          notes: notes,
          fromname: fromname,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            "Track emailed successfully.",
            ToastrType.Success,
          );

          playerService.$emit("track-shared", track, email, notes);

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#shareTrackModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "Error: " + responseData.responseJSON.message,
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Emails a cue sheet to a person with optional notes.
     *
     * @param project
     * @param email
     * @param useremail
     * @param trackNotes
     * @param trackDuration
     * @returns {boolean}
     */
    emailCueSheet: function (
      project,
      email,
      useremail,
      trackNotes,
      trackDuration,
    ) {
      // Check this is a valid project and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_51035] Error: Could not email Project. Invalid project given.",
            ToastrType.Error,
          );
        } else {
          this.toast(
            "[PLYR_51035] Error: Could not email Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this email/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/projects/${project.id}/email-cue-sheet`,
        data: {
          project: project.id,
          email: email,
          useremail: useremail,
          trackNotes: trackNotes,
          trackDuration: trackDuration,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            `Cue sheet emailed to: ${email}.`,
            ToastrType.Success,
          );

          playerService.$emit(
            "cue-sheet-shared",
            project,
            email,
            useremail,
            trackNotes,
            trackDuration,
          );

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#emailCueSheetModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_51034] Error: Could not share Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "[PLYR_51034] Error: Could not share Project.",
              ToastrType.Error,
            );
          }
        });
    },

    fileDownload: function (url, filename) {
      fetch(url).then(function (t) {
        return t.blob().then((b) => {
          var a = document.createElement("a");
          a.href = URL.createObjectURL(b);
          a.setAttribute("download", filename);
          a.click();
        });
      });
    },

    /**
     * Downloads a cue sheet.
     *
     * @param project
     * @param trackNotes
     * @param trackDuration
     * @returns {boolean}
     */
    downloadCueSheet: function (project, trackNotes, trackDuration) {
      // Check this is a valid project and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_51035] Error: Could not save Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this save/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/projects/${project.id}/download-cue-sheet`,
        data: {
          project: project.id,
          trackNotes: trackNotes,
          trackDuration: trackDuration,
        },
        cache: false,
      })
        .done(function (url) {
          // Announce the success of this action.
          playerService.toast(
            `${project.name} Cue Sheet Download will now initialise.`,
            ToastrType.Success,
          );

          // Redirect to the download.
          playerService.fileDownload(
            `/api/projects/${project.id}/download-cue-sheet`,
            url.data,
          );

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#emailCueSheetModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_151324] Error: Could not initialise the download for project cue sheet.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Saves a cue sheet.
     *
     * @param project
     * @param trackNotes
     * @param trackDuration
     * @returns {boolean}
     */
    saveCueSheet: function (project, trackNotes, trackDuration) {
      // Check this is a valid project and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_51035] Error: Could not save Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Send this save/share req to the server to handle & notify the user of the response.
      $.ajax({
        method: "POST",
        url: `/api/projects/${project.id}/save-cue-sheet`,
        dataType: "JSON",
        data: {
          project: project.id,
          trackNotes: trackNotes,
          trackDuration: trackDuration,
        },
        cache: false,
      })
        .done(function (source) {
          // Announce the success of this action.
          playerService.toast(
            `${source.data.name} cue sheet saved.`,
            ToastrType.Success,
          );

          playerService.$emit("project-updated-" + source.id, source);

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#emailCueSheetModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_151301] Error: Could not save Project cue sheet.",
              ToastrType.Error,
            );
          }
        });
    },

    featureRequiresAuthenticationCheck: function () {
      if (window.authenticatedUserEmail == false) {
        playerService.$emit("feature-requires-auth-modal");

        return false;
      }

      return true;
    },

    /**
     * Opens the screen to choose project to add this track to.
     *
     * @param track
     */
    openAddProjectTrack: function (track) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      playerService.$emit("add-project-track", track);
    },

    /**
     * Opens the screen to view track details.
     *
     * @param track
     */
    openTrackDetails: function (track) {
      playerService.$emit("open-track-details", track);
    },

    /**
     * Opens the screen to choose project to add this track to.
     *
     * @param track
     */
    openTrackLyrics: function (track) {
      playerService.$emit("open-track-lyrics", track);
    },

    /**
     * Opens the screen to license a track.
     *
     * @param track
     */
    openLicenseTrack: function (track) {
      playerService.$emit("open-license-track", track);
    },

    /**
     * Opens the screen to manage projects.
     */
    openManageProjects: function () {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      playerService.$emit("open-manage-projects");
    },

    /**
     * Add a Track to a Project.
     *
     * @param project
     * @param track
     * @returns {boolean}
     */
    addProjectTrack: function (project, track) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof project === "undefined" || typeof project.id === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not add Track to Project. Invalid project given.",
            ToastrType.Error,
          );
        } else {
          this.toast(
            "Error: Could not add Track to Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      } else if (
        typeof track === "undefined" ||
        typeof track.id === "undefined"
      ) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not add Track to Project. Invalid track given.",
            ToastrType.Error,
          );
        } else {
          this.toast(
            "Error: Could not add Track to Project. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      // Pass it over to the function that handles this.
      this.addProjectTrackByIds(project.id, track.id);
    },

    /**
     * Add a Track to a Project using Ids only.
     *
     * @param project
     * @param track
     * @returns {boolean}
     */
    addProjectTrackByIds: function (projectId, trackId) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof projectId === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not add Track to Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      if (typeof trackId === "undefined") {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not add Track to Project. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      // Remove it from the database.
      $.ajax({
        method: "POST",
        url: `/api/projects/` + projectId + `/add-track`,
        data: {
          track: trackId,
        },
        cache: false,
      })
        .done(function (project) {
          playerService.$emit("project-updated", project.data);

          if (projectId == window.authenticatedUserQuicklistId) {
            playerService.$emit("project-updated-" + projectId, project.data);
          } else {
            playerService.$emit("project-updated-" + projectId, project.data);
          }

          if (project.data.id == window.authenticatedUserQuicklistId) {
            $(".track-list-row-" + trackId)
              .parent()
              .addClass("in-quicklist");
          }

          // Auto close the edit modal.
          window.webpackCloseBsModalWorkaround("#addProjectTrackModal");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_61540] " + responseData.responseJSON.message,
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Remove a Track from a Project.
     *
     * @returns {boolean}
     * @param projectId
     * @param track
     */
    removeProjectTrack: function (
      projectId,
      trackId,
      removeCompletely = false,
    ) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof projectId === "undefined" || projectId === false) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not remove Track from Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      } else if (typeof trackId === "undefined" || trackId === false) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not remove Track from Project. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      // If the project is the users quicklist then remove that quicklist class on trackrows
      if (projectId == window.authenticatedUserQuicklistId) {
        for (let trackIndex in window.quicklistTrackIds) {
          if (window.quicklistTrackIds.hasOwnProperty(trackIndex)) {
            if (window.quicklistTrackIds[trackIndex] == trackId) {
              window.quicklistTrackIds.splice(trackIndex, 1);

              let $trackListRow = window.jqueryWrapper(
                ".track-list-row-" + trackId,
              );

              if ($trackListRow.length) {
                $trackListRow.parent().removeClass("in-quicklist");

                if (
                  $trackListRow
                    .closest("div.card-track-row")
                    .hasClass("card-subtrack-row")
                ) {
                  if (
                    $trackListRow
                      .closest("div.card-track-parent-row")
                      .find(
                        ".card-subtrack-row .track-list-row-template-container.in-quicklist",
                      ).length < 1
                  ) {
                    $trackListRow
                      .closest("div.card-track-parent-row")
                      .find(".collapse-toggle i")
                      .removeClass("sub-track-in-quicklist");
                  }
                }
              }
            }
          }
        }

        playerService.$emit(`track-list-resource-updated-${projectId}`, {
          collectionTotal: Array.isArray(window.quicklistTrackIds)
            ? window.quicklistTrackIds.length
            : 0,
        });
      }

      let ctx = this;

      // Remove it from the database.
      $.ajax({
        method: "POST",
        url: `/api/projects/` + projectId + `/remove-track`,
        data: {
          track: trackId,
        },
        cache: false,
      })
        .done(function (project) {
          playerService.$emit("project-updated", project.data);
          // Don't want to reload the player when deleting a track from a playlist
          //playerService.$emit("project-updated-" + projectId, project.data);

          if (
            removeCompletely === true &&
            projectId == window.authenticatedUserQuicklistId
          ) {
            playerService.$emit(
              `project-remove-track-completely-${projectId}`,
              trackId,
            );
          } else {
            playerService.$emit(`project-remove-track-${projectId}`, trackId);
          }

          if (
            projectId == window.authenticatedUserQuicklistId ||
            projectId === "quicklistGuest"
          ) {
            playerService.toast(
              `Track has been removed from Quicklist.`,
              ToastrType.Success,
              {
                timeOut: 1250,
                extendedTimeOut: 1000,
              },
            );
          }
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_61540] Error: Could not remove Track from Project.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "[PLYR_61540] Error: Could not remove Track from Project.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Remove a Track from a Project.
     *
     * @returns {boolean}
     * @param album_id
     */
    removeFavouriteAlbum: function (album_id) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof album_id === "undefined" || album_id === false) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not remove Track from Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      let $albumCardContainer = $(
        ".album-card-container-" + album_id + " .favourite",
      ).removeClass("favourited");
      $(".now-playing-album-" + album_id + "-favourite").removeClass(
        "favourited",
      );

      // Remove it from the database.
      $.ajax({
        method: "POST",
        url: `/api/favourite-albums/` + album_id,
        cache: false,
        data: {
          _method: "delete",
        },
      })
        .done(function (source) {
          let favAlbums = [];

          for (let favAlbum of source.data) {
            if (favAlbum.album.id != album_id) {
              favAlbums.push(favAlbum.album.id);
            }
          }

          window.favAlbums = favAlbums;

          // Update our fav albums count to anyone subbed.
          ctx.$emit("fav-albums-count-updated", favAlbums.length);

          ctx.$emit("fav-album-unfavourited", album_id);

          // playerService.$emit( 'update-favourite-albums-listing' );//
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_151335] Error: Could not unfavourite album.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not unfavourite album.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Load Favourite ALbums
     *
     * @returns {boolean}
     */
    loadFavouriteAlbums: function () {
      let ctx = this;

      $.ajax({
        method: "GET",
        url: `/api/favourite-albums`,
        cache: false,
      })
        .done(function (source) {
          let favAlbums = [];

          for (let favAlbum of source.data) {
            favAlbums.push(favAlbum.album.id);
          }

          window.favAlbums = favAlbums;

          // Update our fav albums count to anyone subbed.
          playerService.$emit("fav-albums-count-updated", favAlbums.length);

          playerService.$emit("update-favourite-albums-listing");
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_221525] Error: Could not load fav album,",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not load fav album,",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Add a Track from a Project.
     *
     * @returns {boolean}
     * @param album_id
     */
    addFavouriteAlbum: function (album_id) {
      if (this.featureRequiresAuthenticationCheck() == false) {
        return false;
      }

      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof album_id === "undefined" || album_id === false) {
        if (window.adminProjectShare == true) {
          this.toast(
            "[PLYR_61539] Error: Could not remove Track from Project. Invalid project given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      let ctx = this;

      let $albumCardContainer = $(
        ".album-card-container-" + album_id + " .favourite",
      );

      if ($albumCardContainer.length) {
        $albumCardContainer.addClass("favourited");
      }

      let $nowPlayingAlbumFav = $(
        ".now-playing-album-" + album_id + "-favourite",
      );

      if ($nowPlayingAlbumFav.length) {
        $nowPlayingAlbumFav.addClass("favourited");
      }

      // Add it to the database.
      $.ajax({
        method: "POST",
        url: `/api/favourite-albums`,
        cache: false,
        data: {
          album_id: album_id,
        },
      })
        .done(function (source) {
          let favAlbums = [];

          for (let favAlbum of source.data) {
            favAlbums.push(favAlbum.album.id);
          }

          window.favAlbums = favAlbums;

          playerService.$emit("update-favourite-albums-listing");

          // Update our fav albums count to anyone subbed.
          ctx.$emit("fav-albums-count-updated", favAlbums.length);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_151335] Error: Could not favourite album.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not favourite album.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Opens a playlist via id.
     *
     * @param playlistId
     */
    openPlaylist: function (playlistId) {
      let ctx = this;

      // Fix for BB-1851 / BB-1853 2. When loading a playlist the track results list is not scrolled to the top if it was previously scrolled down
      // Note: Other fixes was tried but didn't work.
      if (typeof window.searchTrackResultsTrackListRef !== "undefined") {
        window.webpackOpenBsTabWorkaround("#search-track-results-tab");
        window.searchTrackResultsTrackListRef.reset();
      }

      $.ajax({
        method: "GET",
        url: `/api/playlists/${playlistId}`,
        cache: false,
      })
        .done(function (source) {
          let playlist = source.data;

          playerService.$emit("playlist-opened", playlist);

          let playlistData = {
            type: "Playlist",
            id: playlist.id,
            name: playlist.name,
          };

          playerService.setUrlHistory(playlistData);

          playerService.setSearchHistory(
            "Playlist",
            playlist.id,
            playlist.name,
          );
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_111038] Error: Could not load Playlist.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not load Playlist.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Opens a playlist via id.
     *
     * @param playlistId
     */
    openSharedPlaylist: function (playlistId) {
      let ctx = this;

      $.ajax({
        method: "GET",
        url: `/api/playlists/shared/${playlistId}`,
        cache: false,
      })
        .done(function (source) {
          let playlist = source.data;

          playerService.$emit("shared-playlist-opened", playlist);

          // Switch to this tab.
          window.webpackOpenBsTabWorkaround("#search-track-results-tab");

          let playlistData = {
            type: "Playlist",
            id: playlist.id,
            name: playlist.name,
            shared: true,
          };

          playerService.setUrlHistory(playlistData);

          playerService.setSearchHistory(
            "Playlist",
            playlist.id,
            playlist.name,
          );
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_111038] Error: Could not load Playlist.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not load Playlist.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Opens a composer via name.
     *
     * @param composerName
     */
    openComposerTracks: function (composerName) {
      playerService.$emit("open-composer-tracks", composerName);

      //let ctx = this;

      /*$.ajax( {
       method: 'POST',
       url: `/api/tracksearchstringbyfield`,
       data: {
       query: composerName,
       default_field: 'composer',
       },
       cache: false
       } )
       .done( function( source ) {

       let tracks = source.data.data;

       playerService.toast(
       'Showing Track results for Composer: ' + composerName,
       ToastrType.Info
       );

       playerService.$emit( 'composer-tracks-opened', tracks );

       } )
       .fail( function( responseData ) {

       playerService.toast(
       '[PLYR_291007] Error: Could not load Composer.',
       ToastrType.Error
       );

       } );*/
    },

    /**
     * Gets the tracks for this track title? Without going through search or filtering traditionaly.
     *
     * @param trackTitle
     */
    openTrackTitleTracks: function (trackTitle) {
      playerService.$emit("open-tracktitle-tracks", trackTitle);
    },

    /**
     * Gets the track results for this track via ID. Without going through search or filtering traditionaly.
     *
     * @param id
     */
    openTrackResultsForId: function (id) {
      playerService.$emit("show-track-results-for-id", id);
    },

    /**
     * Opens an Album via id.
     *
     * @param albumId
     */
    openAlbum: function (albumId) {
      this.$emit("set-invisible-filters", {
        id: "album.id",
        model: "album",
        modelField: "name",
        name: "Album",
        namePlural: "Albums",
        values: [albumId],
      });
    },

    /**
     * Opens the Track List with the contents of the specified Album, without changing the Album Results tab.
     *
     * @param albumId
     * @param showTab
     * @param clearFilters
     */
    trackResultsFromAlbum: function (
      albumId,
      showTab = false,
      clearFilters = true,
    ) {
      this.$emit(
        "trackresults-from-resource",
        {
          id: "album.id",
          model: "album",
          modelField: "name",
          name: "Album",
          namePlural: "Albums",
          values: [albumId],
          clearFilters: clearFilters,
        },
        showTab,
      );
    },

    /**
     * Opens the Track List with the contents of the specified Genre, without changing the Album Results tab.
     *
     * @param genreId
     * @param showTab
     */
    trackResultsFromGenre: function (genreId, showTab = false) {
      console.log("Genre load track results for Genre");

      this.$emit(
        "trackresults-from-resource",
        {
          id: "genre.id",
          model: "genre",
          modelField: "name",
          name: "Genre",
          namePlural: "Genres",
          values: [genreId],
        },
        showTab,
      );
    },

    /**
     * Opens the Album List with the contents of the specified Genre, without changing the Track Results tab.
     *
     * @param genreId
     * @param showTab
     */
    albumResultsFromGenre: function (genreId, showTab = false) {
      this.$emit(
        "albumresults-from-resource",
        {
          id: "genre.id",
          model: "genre",
          modelField: "name",
          name: "Genre",
          namePlural: "Genres",
          values: [genreId],
        },
        showTab,
      );
    },
    /**
     * Opens the Track List with the contents of the specified Composer, without changing the Album Results tab.
     *
     * @param composerId
     * @param showTab
     */
    trackResultsFromComposer: function (
      composerId,
      showTab = false,
      clearFilters = true,
    ) {
      this.$emit(
        "trackresults-from-resource",
        {
          id: "composer",
          model: "composer",
          modelField: "name",
          name: "Composer",
          namePlural: "Composers",
          values: composerId,
          clearFilters: clearFilters,
        },
        showTab,
      );
    },

    /**
     * Opens the Album List with the contents of the specified Composer, without changing the Track Results tab.
     *
     * @param composerId
     * @param showTab
     */
    albumResultsFromComposer: function (composerId, showTab = false) {
      this.$emit(
        "albumresults-from-resource",
        {
          id: "composer",
          model: "composer",
          modelField: "name",
          name: "Composer",
          namePlural: "Composers",
          values: composerId,
        },
        showTab,
      );
    },

    /**
     * Opens an Artist via id.
     *
     * @param artistId
     */
    openArtist: function (artistId) {
      let ctx = this;

      $.ajax({
        method: "GET",
        url: `/api/artists/${artistId}`,
        cache: false,
      })
        .done(function (source) {
          let artist = source.data;

          playerService.$emit("artist-opened", artist);

          let artistData = {
            type: "Artist",
            id: artist.id,
            name: artist.name,
          };

          playerService.setUrlHistory(artistData);

          playerService.setSearchHistory("Artist", artist.id, artist.name);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_120932] Error: Could not load Artist.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not load Artist.",
              ToastrType.Error,
            );
          }
        });
    },

    /**
     * Opens an Library via id.
     *
     * @param libraryId
     * @param clearAlbums
     */
    openLibrary: function (libraryId, clearAlbums = true) {
      if (clearAlbums) {
        playerService.$emit("reset-album-results");
      }

      $.ajax({
        method: "GET",
        url: `/api/libraries/${libraryId}`,
        cache: false,
      })
        .done(function (source) {
          let library = source.data;

          playerService.$emit("library-opened", library);

          let libraryData = {
            type: "Library",
            id: library.id,
            name: library.name,
          };

          playerService.setUrlHistory(libraryData);

          playerService.setSearchHistory("Library", library.id, library.name);
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "[PLYR_120932] Error: Could not load Library.",
              ToastrType.Error,
            );
          } else {
            playerService.toast(
              "Error: Could not load Library.",
              ToastrType.Error,
            );
          }
        });
    },

    loadRandomTrack: function (shouldNotUpdateNowplaying = false) {
      let ctx = this;

      $.ajax({
        method: "GET",
        url: `/api/randomtrack`,
        cache: false,
      })
        .done(function (track) {
          let parsedUrlParams = UrlParamsRetreiver.get(window.location.href);
          let isSharedProject =
            typeof parsedUrlParams["view-shared-project"] !== "undefined";

          if (!isSharedProject) {
            let newTrack = track;
            newTrack.noAutoplay = true;

            if (shouldNotUpdateNowplaying == true) {
              newTrack.shouldNotUpdateNowplaying = shouldNotUpdateNowplaying;
            }

            // Create a timestamp
            audioService.stream(newTrack, true);
          }
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "Error. Could not find random track.",
              ToastrType.Error,
            );
          }
        });
    },

    loadRandomTrackonDemand: function (autoplay = false) {
      let ctx = this;

      $.ajax({
        method: "GET",
        url: `/api/randomtrack`,
        cache: false,
      })
        .done(function (track) {
          let parsedUrlParams = UrlParamsRetreiver.get(window.location.href);
          let isSharedProject =
            typeof parsedUrlParams["view-shared-project"] !== "undefined";

          if (!isSharedProject) {
            let newTrack = track;

            if (autoplay == false) {
              track.noAutoplay = true;
            }

            audioService.stream(track);
          }
        })
        .fail(function (responseData) {
          if (window.adminProjectShare == true) {
            playerService.toast(
              "Error. Could not find random track on demand.",
              ToastrType.Error,
            );
          }
        });
    },

    setLs: function (key, value, duration) {
      Vue.ls.set(key, value, duration);
    },

    getLs: function (key, value) {
      return Vue.ls.get(key, "");
    },

    setUrlHistory: function (searchTerm) {
      const url = new URL(window.location);
      let newUrl =
        url.origin +
        "/player?" +
        searchTerm.type.toLowerCase() +
        "=" +
        searchTerm.id +
        "&name=" +
        searchTerm.name;

      // Add shared parameter if this is a shared resource
      if (searchTerm.shared === true) {
        newUrl += "&shared=true";
      }

      if (url.href !== newUrl) {
        console.log(
          "window location " +
            searchTerm.type.toLowerCase() +
            " is not same as last one",
        );
        window.history.pushState(null, "", newUrl);
      }
    },

    /**
     * Add the searched term to the history
     * @param {*} type String added to frontend describing the type of search
     * @param {*} id Id of the object added to the search
     * @param {*} name Name of the object added to the search displayed on frontend
     */
    setSearchHistory: function (type, id, name) {
      let searchData = {
        type: type,
        id: id,
        name: name,
      };

      let searchHistory = [];

      // Looop through our search history, if the current search value exists, move it to the top.
      if (playerService.getLs("search")) {
        searchHistory = JSON.parse(playerService.getLs("search"));
        for (let value in searchHistory) {
          if (
            JSON.stringify(searchHistory[value]) == JSON.stringify(searchData)
          ) {
            searchHistory.splice(value, 1);
          }
        }
      } else {
        searchHistory = [];
      }

      searchHistory.unshift(searchData);

      playerService.setLs(
        "search",
        JSON.stringify(searchHistory),
        60 * 60 * 24000,
      );
    },
  },
});
